import type { ReactNode } from "react";

import type { SummaryStructureRegistry } from "@/api/summaryStructure";
import {
  SUMMARY_CONCURRENCY_LABEL,
  SUMMARY_LAYER_LABEL,
  SUMMARY_OWNERSHIP_DETAIL,
  SUMMARY_OWNERSHIP_LABEL,
  SUMMARY_VERSIONING_LABEL,
} from "@/lib/labels";
import { layerControls, type SummaryLayerControl } from "@/lib/summaryStructure";
import { Badge } from "@/ui/Badge";

import { Identity } from "./SummaryIdentity";

/**
 * The three-layer control matrix (#640 over #639's registry).
 *
 * ## What it answers
 *
 * One row per ratified Summary layer, and for each: who owns its data, what
 * versions it, how stale access to it fails, where it is read, and — the
 * column this ticket exists for — whether a client may write it and where. The
 * ratified contract (§2.2) says prompt authoring, runtime projection and
 * presentation are separate versioned layers and that "a UI form or a prompt
 * blob is not the runtime clinical record"; before this surface, nothing in the
 * browser said which of the three a clinician was looking at.
 *
 * ## Every cell is the server's
 *
 * `layerControls` derives the control column from `writeRoute` alone, and
 * every other cell is a label for a contract enum. There is no browser-side
 * table of what is editable — that table would be a second copy of the
 * ownership model, and the ticket's non-goals name client-side policy
 * duplication explicitly. The layers are rendered in the server's declaration
 * order, which runs authoring → runtime → presentation: the order the layers
 * act in, and not a sort this component chose.
 *
 * ## No control lives here
 *
 * The matrix STATES where each layer's write surface is; it renders none. The
 * prompt layer's Update and Unpin controls are the section adoption panel's on
 * the same screen, and a second set here would be two authorities for one
 * version-sensitive clinical write — the exact defect #570 removed when it
 * de-duplicated the adopted-item controls. Route templates are shown so a
 * reader can see that the write surface named is the library's and not this
 * screen's.
 */
export function SummaryLayerMatrix({
  registry,
}: {
  registry: SummaryStructureRegistry;
}) {
  const layers = layerControls(registry);

  return (
    <section className="space-y-3" aria-labelledby="summary-layer-matrix">
      <div className="space-y-1">
        <h3
          id="summary-layer-matrix"
          className="text-sm font-semibold text-foreground"
        >
          The three Summary layers
        </h3>
        <p className="text-sm text-muted-foreground">
          Prompt authoring, the protected runtime projection, and presentation
          are separate versioned contracts. Each row states who owns that
          layer&apos;s values, what versions them, how a stale read or write
          fails, and whether this deployment publishes a write surface for
          them.
        </p>
      </div>

      <ul className="space-y-3">
        {layers.map((layer) => (
          <li key={layer.layer}>
            <LayerRow layer={layer} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function LayerRow({ layer }: { layer: SummaryLayerControl }) {
  const writable = layer.control === "server_write_surface";

  return (
    <div className="space-y-2 rounded-md border border-border p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
          {SUMMARY_LAYER_LABEL[layer.layer]}
        </span>
        <Badge tone={layer.ownership === "clinician_editable" ? "accent" : "outline"}>
          {SUMMARY_OWNERSHIP_LABEL[layer.ownership]}
        </Badge>
        {/*
          The control state, in words rather than by tone alone. `warning` is
          deliberately not used for "no write surface": a protected runtime
          record and an ungoverned presentation field are both correct states,
          not warnings, and colouring them as problems would misdescribe the
          contract.
        */}
        <Badge tone={writable ? "success" : "neutral"}>
          {writable ? "Write surface published" : "No write surface"}
        </Badge>
      </div>

      <p className="text-sm text-muted-foreground">
        {SUMMARY_OWNERSHIP_DETAIL[layer.ownership]}
      </p>

      <dl className="grid gap-x-4 gap-y-2 text-xs sm:grid-cols-2">
        <Coordinate label="Versioned by">
          {SUMMARY_VERSIONING_LABEL[layer.versioning]}
        </Coordinate>
        <Coordinate label="Stale access">
          {SUMMARY_CONCURRENCY_LABEL[layer.concurrency]}
        </Coordinate>
        <Coordinate label="Read at">
          <Identity>{layer.readRoute}</Identity>
        </Coordinate>
        <Coordinate label="Written at">
          {layer.writeRoute == null ? (
            <span className="text-muted-foreground">
              {/*
                The two null cases have different reasons and the contract
                states both, so neither is rendered as the other. Told apart by
                the LAYER rather than by a flag, because the reason belongs to
                the layer: only the runtime projection changes through adoption,
                and only the presentation layer is awaiting a governed surface.
              */}
              {layer.layer === "runtime_projection"
                ? "No direct writes. Runtime values change only through exact-version adoption and base refresh, each with its own compare-and-set identity."
                : layer.layer === "presentation_rendering"
                  ? "No write surface in this registry version. The fields below are declared with their defaults; per-project presentation state is a separately governed surface that this deployment does not publish."
                  : "No write surface is published for this layer."}
            </span>
          ) : (
            <Identity>{layer.writeRoute}</Identity>
          )}
        </Coordinate>
      </dl>
    </div>
  );
}

function Coordinate({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-foreground">{children}</dd>
    </div>
  );
}
